import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const DocumentEditor = ({ 
  content, 
  onContentChange, 
  isPreviewMode, 
  onTogglePreview,
  selectedSection,
  onSectionSelect 
}) => {
  const [showFormatting, setShowFormatting] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const editorRef = useRef(null);

  const formatOptions = [
    { value: 'h1', label: 'Heading 1' },
    { value: 'h2', label: 'Heading 2' },
    { value: 'h3', label: 'Heading 3' },
    { value: 'p', label: 'Paragraph' },
    { value: 'ul', label: 'Bullet List' },
    { value: 'ol', label: 'Numbered List' }
  ];

  const legalTemplates = [
    { value: 'data-collection', label: 'Data Collection Clause' },
    { value: 'data-usage', label: 'Data Usage Terms' },
    { value: 'user-rights', label: 'User Rights Section' },
    { value: 'cookies', label: 'Cookie Policy' },
    { value: 'third-party', label: 'Third Party Sharing' },
    { value: 'retention', label: 'Data Retention Policy' }
  ];

  const handleFormatText = (format) => {
    const selection = window.getSelection();
    if (selection?.rangeCount > 0) {
      document.execCommand(format, false, null);
      onContentChange(editorRef?.current?.innerHTML);
    }
  };

  const insertTemplate = (templateType) => {
    const templates = {
      'data-collection': `<h3>Data Collection</h3>\n<p>We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support. This may include your name, email address, phone number, and payment information.</p>`,
      'data-usage': `<h3>How We Use Your Information</h3>\n<p>We use the information we collect to provide, maintain, and improve our services, process transactions, send communications, and comply with legal obligations.</p>`,
      'user-rights': `<h3>Your Rights</h3>\n<p>You have the right to access, update, or delete your personal information. You may also opt out of certain communications and request data portability where applicable.</p>`,
      'cookies': `<h3>Cookies and Tracking</h3>\n<p>We use cookies and similar technologies to enhance your experience, analyze usage patterns, and deliver personalized content. You can control cookie settings through your browser.</p>`,
      'third-party': `<h3>Information Sharing</h3>\n<p>We do not sell your personal information. We may share information with service providers, legal authorities when required, or in connection with business transfers.</p>`,
      'retention': `<h3>Data Retention</h3>\n<p>We retain your information for as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce agreements.</p>`
    };

    if (templates?.[templateType]) {
      const currentContent = editorRef?.current?.innerHTML;
      const newContent = currentContent + templates?.[templateType];
      editorRef.current.innerHTML = newContent;
      onContentChange(newContent);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.ctrlKey || e?.metaKey) {
      switch (e?.key) {
        case 'b':
          e?.preventDefault();
          handleFormatText('bold');
          break;
        case 'i':
          e?.preventDefault();
          handleFormatText('italic');
          break;
        case 'u':
          e?.preventDefault();
          handleFormatText('underline');
          break;
      }
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-card border border-border rounded-lg overflow-hidden">
      {/* Editor Toolbar */}
      <div className="border-b border-border p-4 bg-muted/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="FileText" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Privacy Policy Editor</h2>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant={isPreviewMode ? "outline" : "default"}
              size="sm"
              onClick={onTogglePreview}
              iconName={isPreviewMode ? "Edit" : "Eye"}
              iconPosition="left"
            >
              {isPreviewMode ? "Edit" : "Preview"}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFormatting(!showFormatting)}
              iconName="Settings"
            />
          </div>
        </div>

        {/* Formatting Toolbar */}
        {!isPreviewMode && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center space-x-1 border-r border-border pr-2">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => handleFormatText('bold')}
                iconName="Bold"
                title="Bold (Ctrl+B)"
              />
              <Button
                variant="ghost"
                size="xs"
                onClick={() => handleFormatText('italic')}
                iconName="Italic"
                title="Italic (Ctrl+I)"
              />
              <Button
                variant="ghost"
                size="xs"
                onClick={() => handleFormatText('underline')}
                iconName="Underline"
                title="Underline (Ctrl+U)"
              />
            </div>

            <div className="flex items-center space-x-1 border-r border-border pr-2">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => handleFormatText('insertUnorderedList')}
                iconName="List"
                title="Bullet List"
              />
              <Button
                variant="ghost"
                size="xs"
                onClick={() => handleFormatText('insertOrderedList')}
                iconName="ListOrdered"
                title="Numbered List"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Select
                options={legalTemplates}
                placeholder="Insert Template"
                onChange={(value) => insertTemplate(value)}
                className="w-48"
              />
            </div>
          </div>
        )}
      </div>
      {/* Editor Content */}
      <div className="flex-1 overflow-hidden">
        {isPreviewMode ? (
          <div className="h-full overflow-y-auto p-6 bg-background">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-slate max-w-none">
                <div 
                  dangerouslySetInnerHTML={{ __html: content }}
                  className="text-foreground leading-relaxed"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full p-6">
            <div
              ref={editorRef}
              contentEditable
              className="w-full h-full min-h-96 p-4 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none overflow-y-auto"
              onInput={(e) => onContentChange(e?.target?.innerHTML)}
              onKeyDown={handleKeyDown}
              dangerouslySetInnerHTML={{ __html: content }}
              style={{ 
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                lineHeight: '1.6'
              }}
            />
          </div>
        )}
      </div>
      {/* Editor Status */}
      <div className="border-t border-border px-4 py-2 bg-muted/30">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-4">
            <span>Words: {content?.replace(/<[^>]*>/g, '')?.split(' ')?.filter(word => word?.length > 0)?.length}</span>
            <span>Characters: {content?.replace(/<[^>]*>/g, '')?.length}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Save" size={14} />
            <span>Auto-saved at {new Date()?.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentEditor;